import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useMatch } from 'react-router';
import { Card } from '../../../../design-system';
import Alert from '../../../../helpers/alert';
import { successRefetch } from '../../../../helpers/data-loader';
import { findItemViaPathname } from '../../../../helpers/utils';
import AttachmentsBase from '../../../../components/Attachments';
import ModalForm from '../../../../components/ModalForm';
import SingleEntity from '../../../../components/SingleEntity';
import {
  Table,
  TableCardHeader,
  TableBody as TableBodyBase,
} from '../../../../components/Table';
import { ERR_MSG } from '../../../../globalConstants';
import { COLUMNS, MODAL_FIELDS, SINGLE_ENTITY_FIELDS } from './constants';

const Attachments = AttachmentsBase as React.ComponentType<any>;
const TableBody = TableBodyBase as React.ComponentType<any>;

interface AwardsRecognitionPublicityProps {
  data: { id: any; awards: any[]; all_files: any[]; updated_at?: any };
  pathname: string;
  navigate?: any;
  refetch: () => void;
  canEdit: boolean;
}

const AwardsRecognitionPublicity = ({
  data: { id, awards, all_files, updated_at: updatedAt },
  pathname,
  refetch,
  canEdit,
}: AwardsRecognitionPublicityProps) => {
  const navigate = useNavigate();
  const [isModalOpen, toggleModal] = useState(false);
  const [isAttachmentsModalOpen, toggleAttachmentsModal] = useState(null);
  const selectedAward = findItemViaPathname(awards, pathname);
  const onAwardRoute = useMatch(
    `/vault/${id}/awards-recognition-publicity/:id`,
  );

  useEffect(() => {
    if (onAwardRoute && !selectedAward) {
      navigate(-1);
    }
  }, [onAwardRoute, selectedAward, navigate]);

  const handleAwardCreate = async (values) => {
    try {
      await axios.post(`/client/${id}/awards`, values);
      successRefetch(refetch);
    } catch (error) {
      Alert.error(ERR_MSG);
    }
  };

  const handleAwardUpdate = async (values) => {
    try {
      await axios.put(`/client/${id}/awards/${selectedAward.id}`, values);
      successRefetch(refetch);
    } catch (error) {
      Alert.error(ERR_MSG);
    }
  };
  const handleDeleteAward = async (values) => {
    try {
      await axios.delete(`/client/${id}/awards/${values.id}`);
      successRefetch(refetch);
      navigate(`/vault/${id}/awards-recognition-publicity`);
    } catch (error) {
      Alert.error(ERR_MSG);
    }
  };
  return (
    <>
      <Card id="Awards-table">
        <TableCardHeader
          name="Awards/Recognition/Publicity"
          action="Add"
          onClick={() => toggleModal(true)}
          disabled={!canEdit}
          updatedAt={updatedAt}
        />
        <TableBody maxHeight="22rem">
          <Table
            data={awards.map((award) => ({
              ...award,
              handleDeleteAward,
              canEdit,
            }))}
            columns={COLUMNS}
          />
        </TableBody>
        <ModalForm
          title="Add"
          isOpen={isModalOpen}
          onSubmit={handleAwardCreate}
          onClose={() => toggleModal(false)}
          fields={MODAL_FIELDS}
          oneColumn
        />
      </Card>

      {onAwardRoute &&
        (!selectedAward ? null : (
          <>
            <SingleEntity
              data={selectedAward}
              fields={SINGLE_ENTITY_FIELDS}
              onSubmit={handleAwardUpdate}
              onClose={() =>
                navigate(`/vault/${id}/awards-recognition-publicity`)
              }
              canEdit={canEdit}
            />
            <Attachments
              data={all_files.filter(
                ({ model_type, model_id }) =>
                  model_type === 'VaultAwards' && model_id === selectedAward.id,
              )}
              canEdit={canEdit}
              isModalOpen={isAttachmentsModalOpen}
              onClick={() => toggleAttachmentsModal(true)}
              onClose={() => toggleAttachmentsModal(false)}
              modelId={selectedAward.id}
              modelType="VaultAwards"
              clientId={id}
              refetch={refetch}
              maxHeight="15rem"
              disabled={!canEdit}
            />
          </>
        ))}
    </>
  );
};

export default AwardsRecognitionPublicity;
